import React from "react";
import Stack from "@mui/joy/Stack";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Table from '@mui/joy/Table';
import Chip from '@mui/joy/Chip';
import Divider from "@mui/joy/Divider";
import Box from '@mui/material/Box';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import CircularProgress from "@mui/joy/CircularProgress";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SendIcon from '@mui/icons-material/Send';
import HourglassDisabledIcon from '@mui/icons-material/HourglassDisabled';
import TimerIcon from '@mui/icons-material/Timer';
import DoneIcon from '@mui/icons-material/Done';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CancelIcon from '@mui/icons-material/Cancel';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import {NotificationService} from "../../common/notification";
import {useNavigate} from "react-router-dom";
import { offersHistoryApi } from "./orderApi";
import { OfferHistoryInfo, OfferStatus, OfferStatusDescription, OfferCancellationReasonDescription, SteamApiTradeOfferState, SteamApiTradeStatus } from "../../models/order";
import {formatDateTime} from "../../common/dateTime";
import Tooltip from "@mui/joy/Tooltip";
import InfoIcon from '@mui/icons-material/Info';

type DefaultProps = {
    id: number
    open: boolean
    setOpen: (e: boolean) => void
}

const OfferHistory = (props: DefaultProps) => {
    const {id, open, setOpen} = props;
    const navigate = useNavigate();
    const [history, setHistory] = React.useState<OfferHistoryInfo[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (id > 0) {
            loadData(Number(id)).catch(console.error);
        }
    }, [id])

    const loadData = async (id: number) => {
        setLoading(true);
        try {
            let result = await offersHistoryApi(id);
            if (result.result) {
                const res = result.value!;
                setHistory(res)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false);
        }
    }

    const getStatusIcon = (status: OfferStatus): any => {
        switch(status) { 
            case OfferStatus.New: { 
               return <MailOutlineIcon/>;
            }
            case OfferStatus.CanBeSend: {
                return <MarkEmailReadIcon/>;
            }
            case OfferStatus.WaitingForConfirmation: {
                return <HourglassEmptyIcon/>;
            }
            case OfferStatus.WaitingForCancellation: {
                return <HourglassDisabledIcon/>;
            }
            case OfferStatus.Sent: { 
                return <SendIcon/>;
            }
            case OfferStatus.Accepted: { 
                return <DoneIcon/>;
            }
            case OfferStatus.Hold: { 
                return <TimerIcon/>;
            }
            case OfferStatus.Canceled: { 
                return <CancelIcon/>;
            }
            case OfferStatus.InvalidItems: { 
                return <BrokenImageIcon/>;
            }
            case OfferStatus.RolledBack: { 
                return <FastRewindIcon/>;
            }
            case OfferStatus.Undefined:
            default: { 
                return <QuestionMarkIcon/>;
             }
         }
    }

    const getStatusColor = (status: OfferStatus): 'primary' | 'neutral' | 'danger' | 'success' | 'warning' => {
        switch(status) { 
            case OfferStatus.New: { 
               return 'neutral';
            }
            case OfferStatus.CanBeSend:
            case OfferStatus.WaitingForConfirmation:
            case OfferStatus.Sent: { 
                return 'primary';
            }
            case OfferStatus.Accepted: { 
                return 'success';
            }
            case OfferStatus.WaitingForCancellation:
            case OfferStatus.Canceled:
            case OfferStatus.InvalidItems:
            case OfferStatus.RolledBack: { 
                return 'danger';
            }
            case OfferStatus.Hold:
            case OfferStatus.Undefined:
            default: { 
                return 'warning';
             }
         }
    }

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <ModalDialog aria-labelledby="filter-modal"
                         sx={{ marginTop: {xs: '50px'}, overflow: 'scroll' }}>
                <ModalClose/>
                <Typography id="filter-modal" level="h4">История обработки офера #{id}</Typography>
                <Divider sx={{my: 2}}/>
                {loading ? <CircularProgress/>
                :<>
                    <Sheet
                        className="OrderTableContainer"
                        sx={{
                            display: {xs: 'none', sm: 'initial'},
                            width: '100%',
                            borderRadius: 'sm',
                            flexShrink: 1,
                            overflow: 'auto',
                            height: 'auto',
                            maxHeight: '50dvh'
                        }}
                    >
                        <Table
                            aria-labelledby="tableTitle"
                            stickyHeader
                            hoverRow
                            sx={{
                                '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                '--Table-headerUnderlineThickness': '1px',
                                '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                                '--TableCell-paddingY': '4px',
                                '--TableCell-paddingX': '8px'
                            }}
                        >
                            <thead>
                                <tr>
                                    <th style={{width: 90, padding: '12px 6px'}}>Создано</th>
                                    <th style={{width: 150, padding: '12px 6px'}}>Статус</th>
                                    <th style={{width: 200, padding: '12px 6px'}}>Доп.Информация</th>
                                    <th style={{width: 50, padding: '12px 6px'}}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                <Typography level="body-xs">{formatDateTime(row.created)}</Typography>
                                            </Stack>
                                            {row.groupId && <Typography level="body-xs">Группа {row.groupId}</Typography>}
                                        </td>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                <Chip
                                                    variant="soft"
                                                    size="sm"
                                                    startDecorator={getStatusIcon(row.status)}
                                                    color={getStatusColor(row.status)}
                                                >
                                                    {OfferStatusDescription[row.status]}
                                                </Chip>
                                                {row.steamOfferId !== undefined &&
                                                    <Typography level="body-xs">{`SteamOffer #${row.steamOfferId}: ${SteamApiTradeOfferState[row.steamStatus ?? SteamApiTradeOfferState.Unknown]}`}</Typography>}
                                                {row.steamTradeId !== undefined &&
                                                    <Typography level="body-xs">{`SteamTrade #${row.steamTradeId}: ${SteamApiTradeStatus[row.steamTradeStatus ?? SteamApiTradeStatus.Init]}`}</Typography>}
                                            </Stack>
                                        </td>
                                        <td>
                                            <Stack spacing={1} direction={'column'}>
                                                {row.cancellationReason !== undefined
                                                ?<Typography level="body-xs" textColor={'danger.outlinedColor'}>
                                                    {OfferCancellationReasonDescription[row.cancellationReason]}
                                                </Typography>
                                                :<></>}
                                                {row.errorMessage
                                                ?<Typography level="body-xs" textColor={'danger.darkChannel'}>{row.errorMessage}</Typography>
                                                :<></>}
                                            </Stack>
                                        </td>
                                        <td>
                                            <Tooltip
                                                placement="top-end"
                                                variant="outlined"
                                                arrow
                                                title={row.assetsStatesJson}>
                                                <InfoIcon/>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                    <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                        {history.map((listItem) => (
                            <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'start',
                                    }}
                                >
                                    <ListItemContent key={`historyItem-${listItem.created}`} sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                        <div>
                                            <Typography gutterBottom sx={{fontWeight: 600}}>{formatDateTime(listItem.created)}</Typography>
                                            {listItem.groupId && <Typography level="body-xs">Группа: {listItem.groupId}</Typography>}
                                            {listItem.steamOfferId !== undefined &&
                                                <Typography level="body-xs">{`SteamOffer #${listItem.steamOfferId}: ${SteamApiTradeOfferState[listItem.steamStatus ?? SteamApiTradeOfferState.Unknown]}`}</Typography>}
                                            {listItem.steamTradeId !== undefined &&
                                                <Typography level="body-xs">{`SteamTrade #${listItem.steamTradeId}: ${SteamApiTradeStatus[listItem.steamTradeStatus ?? SteamApiTradeStatus.Init]}`}</Typography>}
                                            {listItem.cancellationReason !== undefined
                                            ?<Typography level="body-xs" textColor={'danger.outlinedColor'}>
                                                {OfferCancellationReasonDescription[listItem.cancellationReason]}
                                            </Typography>
                                            :<></>}
                                            {listItem.errorMessage
                                            ?<Typography level="body-xs" textColor={'danger.darkChannel'}>Доп.Информация: {listItem.errorMessage}</Typography>
                                            :<></>}
                                        </div>
                                    </ListItemContent>
                                    <Chip
                                        variant="soft"
                                        size="sm"
                                        startDecorator={getStatusIcon(listItem.status)}
                                        color={getStatusColor(listItem.status)}
                                    >
                                        {OfferStatusDescription[listItem.status]}
                                    </Chip>
                                </ListItem>
                                <ListDivider/>
                            </List>
                        ))}
                    </Box>
                </>}
            </ModalDialog>
        </Modal>
    );
};

export default OfferHistory;